import React, { useCallback, useState, Suspense, lazy } from "react";
import { useAttendance } from "contexts/v1/masterData/AttendanceContext";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import Swal from "sweetalert2";
import "react-datepicker/dist/react-datepicker.css";
import PrintJson from "utils/printJson";
import { 
  IconPlus, 
  IconTable, 
  IconCalendar, 
  IconInfoCircle,
} from "@tabler/icons-react";
import { useUser } from "contexts/v1/masterData/UserContext";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import AttendanceHubToolbar from "./attendance-hub-toolbar";
import BulkAttendanceDevTool from "./dev-tools/BulkAttendanceDevTool";

const CreateAttendancePage = lazy(() => import("./create-attendance-page"));
const AttendanceCalendarV2 = lazy(() => import("./attendance-calendar-v2"));

const AttendanceRow = React.memo(({ record, index, filterOptions, onUpdate, onDelete, formatDateTime, formatDuration, formatDurationSmart }) => {
  return (
    <tr>
      <td>
        {(filterOptions.currentPage - 1) *
          filterOptions.perPage +
          index +
          1}
      </td>
      <td>
        <div className="d-flex align-items-center">
          <div className="avatar avatar-xs me-2 rounded bg-primary-lt text-primary fw-bold">
            {record.user?.name?.charAt(0).toUpperCase()}
          </div>
          <span className="fw-medium">
            {record.user?.name}
          </span>
        </div>
      </td>
      <td>{formatDateTime(record.check_in)}</td>
      <td>{formatDateTime(record.check_out)}</td>
      <td>{formatDuration(record.duration)}</td>
      <td>
        {record.early > 0 ? (
          <span className="badge bg-success-lt text-success border-0">
            {formatDurationSmart(record.early, record, 'early')}
          </span>
        ) : (
          <span className="text-muted opacity-25">-</span>
        )}
      </td>
      {/* Late and Overtime columns hidden as requested */}
      {/* 
      <td>
        {record.late > 0 ? (
          <span className="badge bg-warning-lt text-warning border-0">
            {formatDurationSmart(record.late, record, 'late')}
          </span>
        ) : (
          <span className="text-muted opacity-25">-</span>
        )}
      </td>
      <td>
        {record.overtime > 0 ? (
          <span className="badge bg-info-lt text-info border-0">
            {formatDurationSmart(record.overtime, record, 'overtime')}
          </span>
        ) : (
          <span className="text-muted opacity-25">-</span>
        )}
      </td>
      */}
      <td>
        <div className="btn-list flex-nowrap">
          <button
            className="btn btn-sm btn-white border shadow-xs"
            onClick={(e) => onUpdate(e, record)}
          >
            Edit
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={(e) => onDelete(e, record.id)}
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
});

const AttendanceHubRawTab = () => {
  const { attendance, filterOptions, setFilterOptions, Pagination, refetch, deleteAttendance } =
    useAttendance();
  const { users } = useUser();

  const { handleError, showCanvasHandler, handleSuccessToast } =
    useCanvasHandler();

  const [viewMode, setViewMode] = useState("calendar");
  const [cellWidth, setCellWidth] = useState(45);
  const [nameWidth, setNameWidth] = useState(150);

  const handleGoToToday = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 26);
    const end = new Date(now.getFullYear(), now.getMonth(), 25);

    setFilterOptions(prev => ({
      ...prev,
      startDate: start,
      endDate: end,
      currentPage: 1
    }));
  };

  const handlePrevPeriod = () => {
    if (!filterOptions.startDate) return;
    const start = new Date(filterOptions.startDate);
    start.setMonth(start.getMonth() - 1);
    
    const end = new Date(start);
    end.setMonth(end.getMonth() + 1);
    end.setDate(25);
    
    setFilterOptions(prev => ({
      ...prev,
      startDate: start,
      endDate: end,
      currentPage: 1
    }));
  };

  const handleNextPeriod = () => {
    if (!filterOptions.startDate) return;
    const start = new Date(filterOptions.startDate);
    start.setMonth(start.getMonth() + 1);
    
    const end = new Date(start);
    end.setMonth(end.getMonth() + 1);
    end.setDate(25);
    
    setFilterOptions(prev => ({
      ...prev,
      startDate: start,
      endDate: end,
      currentPage: 1
    }));
  };

  const handleDateChange = (date, type) => {
    const newStartDate = type === "startDate" ? date : filterOptions.startDate;
    const newEndDate = type === "endDate" ? date : filterOptions.endDate;

    if (newStartDate && newEndDate) {
      const diffTime = Math.abs(newEndDate - newStartDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays > 62) {
        Swal.fire({
          title: "Rentang Waktu Terlalu Besar",
          text: "Silakan pilih rentang waktu maksimal 2 bulan (62 hari) untuk menjaga performa aplikasi.",
          icon: "warning",
          confirmButtonColor: "#206bc4",
        });
        return;
      }
    }

    setFilterOptions((prev) => ({
      ...prev,
      [type]: date,
      currentPage: 1
    }));
  };


  const handleCreate = (e, prefilledData = null) => {
    if (e) e.preventDefault();
    showCanvasHandler(
      "Create Attendance Record",
      <Suspense fallback={<div className="p-3 text-center">Loading form...</div>}>
        <CreateAttendancePage
          initialData={prefilledData}
          onSuccess={() => {
            handleSuccessToast(true, "Attendance record created successfully");
            refetch();
          }}
          onError={handleError}
        />
      </Suspense>
    );
  };

  // update attendance record
  const handleUpdate = (e, data) => {
    if (e) e.preventDefault();
    showCanvasHandler(
      "Update Attendance Record",
      <Suspense fallback={<div className="p-3 text-center">Loading form...</div>}>
        <CreateAttendancePage
          initialData={data}
          onSuccess={() => {
            handleSuccessToast(true, "Attendance record updated successfully");
            refetch();
          }}
          onError={handleError}
          onDelete={(e) => handleDelete(e, data.id)}
        />
      </Suspense>
    );
  };

  // delete attendance record
  const handleDelete = async (e, id) => {
    if (e) e.preventDefault();
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteAttendance(id);
        if (response.success) {
          handleSuccessToast(true, "Attendance record deleted successfully");
          refetch();
        } else {
          throw new Error(
            response.message || "Failed to delete attendance record"
          );
        }
      } catch (error) {
        handleError(error);
      }
    }
  };

  const formatDateTime = useCallback((dateTimeString) => {
    if (!dateTimeString) return "-";
    let timestamp;
    if (typeof dateTimeString === "number") {
      timestamp = dateTimeString * 1000;
    } else {
      timestamp = dateTimeString;
    }
    const date = new Date(timestamp);
    return date.toLocaleString("id-ID", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  const formatDuration = useCallback((seconds) => {
    if (!seconds || seconds <= 0) return "-";
    
    if (seconds > 1000000000) {
      return "N/A";
    }
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }, []);

  const formatDurationSmart = useCallback((seconds, record, field) => {
    if (!seconds || seconds <= 0) return "-";
    
    if (seconds > 1000000000) {
      const timestampTime = new Date(seconds * 1000);
      const timeStr = timestampTime.toLocaleTimeString('id-ID', {
        hour: '2-digit',
        minute: '2-digit'
      });
      return `@${timeStr}`;
    }
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }, []);

  const filteredUsersData = React.useMemo(() => {
    if (!users.data) return [];
    let result = users.data;
    
    if (filterOptions.departmentId) {
      result = result.filter(u => u.department?.id == filterOptions.departmentId);
    }
    
    if (filterOptions.userId) {
      result = result.filter(u => u.id == filterOptions.userId);
    }
    
    return result;
  }, [users.data, filterOptions.userId, filterOptions.departmentId]);

  const filteredAttendanceData = React.useMemo(() => {
    if (!attendance.data) return [];
    let result = attendance.data;
    if (filterOptions.departmentId) {
      result = result.filter(record => record.user?.department?.id == filterOptions.departmentId);
    }
    return result;
  }, [attendance.data, filterOptions.departmentId]);

  // Frontend Pagination Logic
  const totalItems = filteredAttendanceData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / filterOptions.perPage));
  
  const FrontendPagination = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: (page) => setFilterOptions(prev => ({ ...prev, currentPage: page })),
    visiblePages: 5,
  });

  const paginatedAttendanceData = React.useMemo(() => {
    const start = (filterOptions.currentPage - 1) * filterOptions.perPage;
    const end = start + filterOptions.perPage;
    return filteredAttendanceData.slice(start, end);
  }, [filteredAttendanceData, filterOptions.currentPage, filterOptions.perPage]);

  // Only show full page loading if we have no data yet
  if (attendance.loading && (!attendance.data || attendance.data.length === 0)) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (attendance.error) {
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          Error loading attendance data: {attendance.error}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-3">
        <AttendanceHubToolbar
          startDate={filterOptions.startDate}
          endDate={filterOptions.endDate}
          departmentId={filterOptions.departmentId}
          userId={filterOptions.userId}
          onDateChange={handleDateChange}
          onPreviousPeriod={handlePrevPeriod}
          onNextPeriod={handleNextPeriod}
          onToday={handleGoToToday}
          onFilterChange={(changes) => setFilterOptions((previous) => ({ ...previous, ...changes, currentPage: 1 }))}
          adjustControls={viewMode === "calendar" ? (
            <>
              <label className="form-label small fw-bold text-muted mb-1">Employee Column ({nameWidth}px)</label>
              <input type="range" className="form-range mb-3" min="100" max="300" step="5" value={nameWidth} onChange={(event) => setNameWidth(Number(event.target.value))} />
              <label className="form-label small fw-bold text-muted mb-1">Daily Column ({cellWidth}px)</label>
              <input type="range" className="form-range mb-0" min="30" max="100" step="5" value={cellWidth} onChange={(event) => setCellWidth(Number(event.target.value))} />
            </>
          ) : null}
          actionControls={(
            <>
              <div className="btn-group shadow-sm">
              <button
                className={`btn btn-sm btn-outline-primary fw-bold ${viewMode === "table" ? "active" : ""}`}
                onClick={() => setViewMode("table")}
              >
                <IconTable size={15} className="me-1" /> Table
              </button>
              <button
                className={`btn btn-sm btn-outline-primary fw-bold ${viewMode === "calendar" ? "active" : ""}`}
                onClick={() => setViewMode("calendar")}
              >
                <IconCalendar size={15} className="me-1" /> Calendar
              </button>
            </div>
              <button className="btn btn-sm btn-primary shadow-sm px-3 fw-bold" onClick={(e) => handleCreate(e)}>
                <IconPlus size={15} className="me-1" /> Create Attendance
            </button>
              <BulkAttendanceDevTool
                startDate={filterOptions.startDate}
                endDate={filterOptions.endDate}
                attendanceData={filteredAttendanceData}
                onSuccess={() => {
                  handleSuccessToast(true, "Bulk attendance finished");
                  refetch();
                }}
              />
            </>
          )}
        />

        {/* 🔹 View Content */}
        {viewMode === "table" ? (
          <div className="card border-0 shadow-sm overflow-hidden">
            <div className="table-responsive">
              <table className="table table-vcenter table-hover card-table">
                <thead>
                  <tr>
                    <th className="w-1">No</th>
                    <th>User</th>
                    <th>Check In</th>
                    <th>Check Out</th>
                    <th>Duration</th>
                    <th>Early</th>
                    <th className="w-1">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAttendanceData?.length > 0 ? (
                    paginatedAttendanceData.map((record, index) => (
                      <AttendanceRow 
                        key={record.id}
                        record={record}
                        index={index}
                        filterOptions={filterOptions}
                        onUpdate={handleUpdate}
                        onDelete={handleDelete}
                        formatDateTime={formatDateTime}
                        formatDuration={formatDuration}
                        formatDurationSmart={formatDurationSmart}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <div className="empty">
                          <div className="empty-icon">
                            <IconInfoCircle size={40} className="text-muted opacity-25" />
                          </div>
                          <p className="empty-title">No records found</p>
                          <p className="empty-subtitle text-muted">
                            Try adjusting your search or filter to find what you're looking for.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {totalItems > 0 && (
              <div className="card-footer d-flex align-items-center justify-content-between bg-white border-top-0">
                <p className="m-0 text-muted small">
                  Showing <span>{(filterOptions.currentPage - 1) * filterOptions.perPage + 1}</span> to <span>{Math.min(filterOptions.currentPage * filterOptions.perPage, totalItems)}</span> of <span>{totalItems}</span> entries
                </p>
                {FrontendPagination}
              </div>
            )}
          </div>
        ) : (
          <div className="card border-0 shadow-sm overflow-hidden min-vh-50">
            <Suspense fallback={
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading Calendar...</span>
                </div>
              </div>
            }>
              <AttendanceCalendarV2
                attendance={attendance}
                usersData={filteredUsersData}
                onCreate={handleCreate}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                cellWidth={cellWidth}
                nameWidth={nameWidth}
              />
            </Suspense>
          </div>
        )}
      </div>

      <style>{`
        .schedule-nav-card {
          box-shadow: 0 8px 24px rgba(0,0,0,0.08) !important;
          border: 1px solid rgba(231, 234, 239, 0.5) !important;
        }
        .ls-1 {
          letter-spacing: 0.05em;
        }
        .bg-primary-lt {
          background-color: rgba(32, 107, 196, 0.08) !important;
          color: #206bc4 !important;
        }
        .bg-success-lt {
          background-color: rgba(47, 179, 68, 0.08) !important;
        }
        .bg-warning-lt {
          background-color: rgba(245, 159, 0, 0.08) !important;
        }
        .bg-info-lt {
          background-color: rgba(66, 153, 225, 0.08) !important;
        }
        .react-datepicker-portal,
        .react-datepicker-popper {
          z-index: 99 !important;
        }
        .card-table thead th {
          background: #f8fafc;
          text-transform: uppercase;
          font-size: 0.65rem;
          font-weight: 700;
          letter-spacing: 0.02em;
          color: #64748b;
          border-top: none;
        }
        .shadow-xs {
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
        }
      `}</style>
      <PrintJson data={attendance} title="Attendance Data" />
    </>
  );
};

export default AttendanceHubRawTab;
